/**
 * Provider-then-driver assignment fields for booking modals.
 * Ops search a provider first, then pick from that provider's drivers.
 */

import React, { useCallback, useEffect, useState } from "react";
import { Form, Select, Space, Tag, Typography } from "antd";
import {
  BankOutlined,
  CheckCircleOutlined,
  IdcardOutlined,
  PhoneOutlined,
  UserAddOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { getPartner, getPartners } from "../../partners/services/partnerService";
import { getDriver, getDrivers } from "../services/driverService";
import {
  BUBAKII_PROVIDER,
  CREATE_DRIVER_OPTION_VALUE,
  driverFiltersForProvider,
  isBubakiiProvider,
  matchesDriverSearch,
  matchesProviderSearch,
  providerFromDriver,
} from "../utils/providerDriverAssignment";

const { Text } = Typography;
const { Option } = Select;

const ProviderDriverAssignmentFields = ({
  form,
  driverRequired = false,
  allowCreateDriver = false,
  onCreateDriver,
  initialProvider = null,
  initialDriverId = null,
  resetKey,
  reloadToken = 0,
  createdDriver = null,
  onProviderChange,
}) => {
  const [partners, setPartners] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);

  const loadPartners = useCallback(async () => {
    setLoadingProviders(true);
    try {
      const result = await getPartners({ isActive: true });
      if (result.success) {
        setPartners(result.data || []);
      } else {
        setPartners([]);
      }
    } catch (error) {
      console.error("Error loading providers:", error);
      setPartners([]);
    } finally {
      setLoadingProviders(false);
    }
  }, []);

  const loadDriversForProvider = useCallback(async (provider) => {
    if (!provider) {
      setDrivers([]);
      return;
    }

    const filters = driverFiltersForProvider(provider);
    if (!filters) {
      setDrivers([]);
      return;
    }

    setLoadingDrivers(true);
    try {
      const result = await getDrivers(filters);
      if (result.success) {
        setDrivers(result.data || []);
      } else {
        setDrivers([]);
      }
    } catch (error) {
      console.error("Error loading provider drivers:", error);
      setDrivers([]);
    } finally {
      setLoadingDrivers(false);
    }
  }, []);

  const initialProviderId = initialProvider?.id || null;

  useEffect(() => {
    loadPartners();
  }, [loadPartners, resetKey]);

  useEffect(() => {
    setSelectedProvider(initialProvider || null);
    if (initialProvider) {
      loadDriversForProvider(initialProvider);
    } else if (!initialDriverId) {
      setDrivers([]);
    }
  }, [initialProviderId, initialDriverId, resetKey, loadDriversForProvider]); // initialProvider is keyed by id

  useEffect(() => {
    if (!reloadToken || !selectedProvider) return;
    loadDriversForProvider(selectedProvider);
    // Only refetch when parent asks for a reload (e.g. after creating a driver).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadToken]);

  useEffect(() => {
    if (!createdDriver?.id) return;
    setDrivers((prev) =>
      prev.some((driver) => driver.id === createdDriver.id)
        ? prev
        : [createdDriver, ...prev]
    );
    form?.setFieldsValue({ driverId: createdDriver.id });
  }, [createdDriver, form]);

  useEffect(() => {
    const resolveFromDriver = async () => {
      if (initialProviderId || !initialDriverId) return;
      try {
        const result = await getDriver(initialDriverId);
        const driver = result.success ? result.data : result;
        if (!driver?.id && !driver?.ownerType) return;

        let partner = driver.partner || null;
        if (driver.ownerType === "third_party" && driver.partnerId && !partner) {
          const partnerResult = await getPartner(driver.partnerId);
          if (partnerResult.success) {
            partner = partnerResult.data;
          }
        }

        const provider = providerFromDriver(driver, partner);
        setSelectedProvider(provider);
        await loadDriversForProvider(provider);
      } catch (error) {
        console.error("Error resolving provider from assigned driver:", error);
      }
    };

    resolveFromDriver();
  }, [initialDriverId, initialProviderId, loadDriversForProvider, resetKey]);

  const handleProviderChange = (providerId) => {
    if (!providerId) {
      setSelectedProvider(null);
      setDrivers([]);
      form?.setFieldsValue({ driverId: undefined });
      onProviderChange?.(null);
      return;
    }

    const provider =
      providerId === BUBAKII_PROVIDER.id
        ? { ...BUBAKII_PROVIDER }
        : partners.find((partner) => partner.id === providerId) || {
            id: providerId,
            name: "Provider",
            ownerType: "third_party",
          };

    setSelectedProvider(provider);
    form?.setFieldsValue({ driverId: undefined });
    onProviderChange?.(provider);
    loadDriversForProvider(provider);
  };

  const handleDriverChange = (driverId) => {
    if (driverId === CREATE_DRIVER_OPTION_VALUE) {
      form?.setFieldsValue({ driverId: undefined });
      onCreateDriver?.(selectedProvider);
    }
  };

  const providerOptions = [BUBAKII_PROVIDER, ...partners];
  const providerHelp = selectedProvider
    ? `Showing drivers for ${selectedProvider.name}.`
    : "Search by company name, contact, or phone, then choose a driver.";

  const driverHelp = !selectedProvider
    ? "Select a provider first to see their drivers."
    : drivers.length === 0 && !loadingDrivers
      ? allowCreateDriver
        ? `No drivers found for ${selectedProvider.name}. You can add one.`
        : `No drivers found for ${selectedProvider.name}.`
      : `Select a driver from ${selectedProvider.name}.`;

  return (
    <>
      <Form.Item
        label="Provider"
        required={driverRequired}
        help={providerHelp}
      >
        <Select
          showSearch
          allowClear
          value={selectedProvider?.id}
          placeholder="Search for a provider..."
          loading={loadingProviders}
          optionFilterProp="label"
          filterOption={(input, option) => {
            const provider =
              providerOptions.find((item) => item.id === option.value) || {
                name: option.label,
              };
            return matchesProviderSearch(provider, input);
          }}
          onChange={handleProviderChange}
          aria-label="Search for a provider"
          data-testid="booking-provider-search"
          optionLabelProp="label"
        >
          {providerOptions.map((provider) => (
            <Option
              key={provider.id}
              value={provider.id}
              label={provider.name}
              provider={provider}
            >
              <Space>
                <BankOutlined />
                <Text strong>{provider.name}</Text>
                <Tag color={isBubakiiProvider(provider) ? "blue" : "orange"}>
                  {isBubakiiProvider(provider) ? "Bubakii" : "3rd Party"}
                </Tag>
              </Space>
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="driverId"
        label="Assign Primary Driver"
        rules={
          driverRequired
            ? [{ required: true, message: "Driver assignment is required" }]
            : []
        }
        help={driverHelp}
      >
        <Select
          showSearch
          allowClear
          placeholder={
            selectedProvider
              ? "Select a driver..."
              : "Select a provider first"
          }
          loading={loadingDrivers}
          notFoundContent={
            !selectedProvider
              ? "Select a provider first"
              : loadingDrivers
                ? "Loading drivers..."
                : "No drivers found"
          }
          optionFilterProp="label"
          filterOption={(input, option) => {
            if (option.value === CREATE_DRIVER_OPTION_VALUE) {
              return true;
            }
            const driver = drivers.find((item) => item.id === option.value);
            return matchesDriverSearch(driver || { name: option.label }, input);
          }}
          onChange={handleDriverChange}
          aria-label="Assign primary driver"
          data-testid="booking-driver-select"
          optionLabelProp="label"
        >
          {allowCreateDriver && selectedProvider ? (
            <Option
              value={CREATE_DRIVER_OPTION_VALUE}
              label={`Add a driver for ${selectedProvider.name}`}
            >
              <Space>
                <UserAddOutlined style={{ color: "#1890ff" }} />
                <Text type="success">
                  Add a driver for {selectedProvider.name}
                </Text>
              </Space>
            </Option>
          ) : null}
          {drivers.map((driver) => {
            const isExpired = driver.licenseExpiry
              ? new Date(driver.licenseExpiry) < new Date()
              : false;

            return (
              <Option key={driver.id} value={driver.id} label={driver.name}>
                <Space direction="vertical" size={0} style={{ width: "100%" }}>
                  <Space>
                    <Text strong>{driver.name}</Text>
                    <Tag
                      color={driver.ownerType === "bubakii" ? "blue" : "orange"}
                    >
                      {driver.ownerType === "bubakii" ? "Bubakii" : "3rd Party"}
                    </Tag>
                  </Space>
                  <Space>
                    <PhoneOutlined style={{ fontSize: "12px" }} />
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      {driver.phone}
                    </Text>
                  </Space>
                  <Space>
                    <IdcardOutlined style={{ fontSize: "12px" }} />
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      License: {driver.licenseNumber || "N/A"}
                    </Text>
                  </Space>
                  <Space>
                    {isExpired ? (
                      <WarningOutlined
                        style={{ fontSize: "12px", color: "#ff4d4f" }}
                      />
                    ) : (
                      <CheckCircleOutlined
                        style={{ fontSize: "12px", color: "#52c41a" }}
                      />
                    )}
                    <Text
                      type={isExpired ? "danger" : "secondary"}
                      style={{ fontSize: "12px" }}
                    >
                      Expiry:{" "}
                      {driver.licenseExpiry
                        ? new Date(driver.licenseExpiry).toLocaleDateString()
                        : "N/A"}
                    </Text>
                  </Space>
                </Space>
              </Option>
            );
          })}
        </Select>
      </Form.Item>
    </>
  );
};

export default ProviderDriverAssignmentFields;
